/**
 * Data-driven route configuration.
 * Single source of truth for public and authenticated routes.
 * Used by UnauthenticatedRoutes, AuthenticatedRoutes, and Sidebar (SIDEBAR_NAV + getRolesForPath).
 */

import { matchPath } from "react-router-dom";
import { TEMPORARILY_HIDDEN_PATHS, isPageVisible } from "./pageVisibility";
import { WORKSPACE_SETTINGS_PATH, LEGACY_SETTINGS_REDIRECTS } from "./workspaceSettings";

/** Default path after login / when visiting "/" or "/login" while authenticated */
export const DEFAULT_ROUTE_BY_ROLE = {
  SUPER_ADMIN: "/dashboard",
  ADMIN: "/finance/owed",
  OPERATOR: "/orders",
};

/**
 * Returns the default route path for the given roles (e.g. from Redux or login response).
 * First matching role wins. Falls back to /orders if no match.
 * @param {string[]} roles - User roles, e.g. ["OPERATOR"] or ["SUPER_ADMIN"]
 * @returns {string} Path to redirect to
 */
export function getDefaultRouteForRoles(roles) {
  if (!Array.isArray(roles) || roles.length === 0) {
    return DEFAULT_ROUTE_BY_ROLE.OPERATOR;
  }
  if (roles.includes("SUPER_ADMIN")) {
    return DEFAULT_ROUTE_BY_ROLE.SUPER_ADMIN;
  }
  if (roles.includes("OPERATOR")) {
    return DEFAULT_ROUTE_BY_ROLE.OPERATOR;
  }
  if (roles.includes("ADMIN")) return DEFAULT_ROUTE_BY_ROLE.ADMIN;
  return DEFAULT_ROUTE_BY_ROLE.OPERATOR;
}

/** Public routes (no auth): path -> dynamic import */
export const PUBLIC_ROUTES = [
  { path: "/subscription/return", component: () => import("@components/Billing/PaymentReturn") },
  { path: "/", component: () => import("@components/Login/Login") },
  { path: "/login", component: () => import("@components/Login/Login") },
  { path: "/registration", component: () => import("@components/Registration/Registration") },
  { path: "/verify-email", component: () => import("@components/VerifyEmail/VerifyEmail") },
  { path: "/accept-invite", component: () => import("@components/AcceptInvite/AcceptInvite") },
  { path: "/reset-password", component: () => import("@components/ResetPassword/ResetPassword") },
  { path: "/terms-of-use", component: () => import("@components/Legal/TermsOfUse") },
  { path: "/privacy-policy", component: () => import("@components/Legal/PrivacyPolicy") },
];

/** Paths that redirect to role default when user is already authenticated */
export const AUTH_REDIRECT_PATHS = ["/", "/login", "/registration", "/verify-email", "/reset-password", ...TEMPORARILY_HIDDEN_PATHS];

/** Workspace settings opens the company page. Legacy accounting paths remain in Operations. */
export const SETTINGS_DEFAULT_PATH = WORKSPACE_SETTINGS_PATH;

/**
 * Operations layout and child routes (path segment only for children).
 *
 * Index (/operations): There is no hard-coded redirect here (e.g. to /operations/invoices).
 * The Operations layout is the single source of truth: it redirects to the first section
 * in SECTION_PATH_ORDER that the user can access (canAccessSection). Operators typically
 * land on Invoices; Super Admins land on the first section they have enabled.
 * Keep sidebar and any "Operations" entry points linking to /operations so the layout
 * controls the landing section.
 *
 * Use redirect for legacy paths (e.g. expense-categories → expense/expense-category).
 */
export const OPERATIONS_CHILDREN = [
  { path: "invoices", component: () => import("@features/operations/views/Invoices/InvoicesView") },
  { path: "expenses", component: () => import("@features/operations/views/Expenses/ExpensesView") },
  { path: "expense-categories", redirect: "/operations/expense/expense-category" },
  { path: "expense/expense-category", component: () => import("@features/operations/views/ExpenseCategories/ExpenseCategoriesView") },
  { path: "investments", component: () => import("@features/operations/views/Investments/InvestmentsView") },
  { path: "investment-categories", redirect: "/operations/investment/investment-category" },
  { path: "investment/investment-category", component: () => import("@features/operations/views/InvestmentCategories/InvestmentCategoriesView") },
  { path: "reporting", component: () => import("@features/operations/views/Reporting/ReportingView") },
  { path: "reporting/account-codes", component: () => import("@components/AccountCodes/AccountCodes") },
  { path: "services", component: () => import("@features/operations/views/Services/ServicesView") },
  { path: "service-companies", redirect: "/operations/service/service-companies" },
  { path: "service-types", redirect: "/operations/service/service-types" },
  { path: "service/service-companies", component: () => import("@features/operations/views/ServiceCompanies/ServiceCompaniesView") },
  { path: "service/service-types", component: () => import("@features/operations/views/ServiceTypes/ServiceTypesView") },
];

/** Standalone authenticated routes (any authenticated user, no role check) */
export const AUTH_STANDALONE_ROUTES = [
  { path: "/accept-invite", component: () => import("@components/AcceptInvite/AcceptInvite") },
  { path: "/unauthorized", component: () => import("@components/Unauthorized/Unauthorized") },
  { path: "/subscription/return", component: () => import("@components/Billing/PaymentReturn") },
];

/**
 * Flattens route groups for lazy loading: routes with children become one layout entry plus one entry per child path.
 */
function flattenRoutesForLazy(groups) {
  return groups.flatMap((g) =>
    g.routes.flatMap((r) => {
      if (r.redirect || !r.component) return [];
      if (r.children) {
        return [
          { path: r.path, component: r.component },
          ...r.children
            .filter((c) => !c.redirect && c.component)
            .map((c) => ({ path: `${r.path}/${c.path}`, component: c.component })),
        ];
      }
      return [r];
    })
  );
}

/**
 * Authenticated route groups. Each group is wrapped in RequireAuth with allowedRoles.
 * Route.wrapper: optional key (e.g. "OrderProvider") to wrap the route element.
 * Route.children: nested routes under this path (layout must render <Outlet />).
 */
export const AUTH_ROUTE_GROUPS = [
  {
    allowedRoles: ["SUPER_ADMIN", "ADMIN"],
    routes: [
      { path: "/finance", redirect: "/finance/owed" },
      { path: "/finance/owed", component: () => import("@features/finance/OwedToUs") },
      { path: "/finance/we-owe", component: () => import("@features/finance/WeOwe") },
      { path: "/finance/orders/:id", component: () => import("@features/finance/FinanceOrder") },
    ],
  },
  {
    allowedRoles: ["OPERATOR", "SUPER_ADMIN"],
    routes: [
      { path: "/search", component: () => import("@components/TravelSearch/SearchWorkspace") },
      { path: "/orders/:id/search", component: () => import("@components/TravelSearch/SearchWorkspace") },
      { path: "/calendar", component: () => import("@components/Calendar/CalendarComponent") },
      { path: "/workplace/:customerId?", component: () => import("@components/WorkPlace/WorkPlace") },
      { path: "/agent", component: () => import("@components/Agent/AgentPage") },
      {
        path: "/operations",
        component: () => import("@features/operations"),
        children: OPERATIONS_CHILDREN,
      },
      { path: "/user-account", component: () => import("@components/UserProfile/UserProfile") },
      { path: "/customers", component: () => import("@components/Customers/Customer") },
      {
        path: "/orders",
        component: () => import("@components/Order/Order"),
        wrapper: "OrderProvider",
      },
      { path: "/invoice", redirect: "/operations/invoices" },
      { path: "/services", redirect: "/operations/services" },
    ],
  },
  {
    allowedRoles: ["SUPER_ADMIN"],
    routes: [
      { path: "/dashboard", component: () => import("@features/dashboard") },
      {
        path: "/settings/company",
        component: () => import("@components/CompanyProfile/CompanyProfileLayout"),
        children: [
          { path: "", component: () => import("@components/CompanyProfile/CompanyProfileGeneral") },
          { path: "branding", component: () => import("@components/CompanyProfile/CompanyProfileGeneral") },
          { path: "bank-accounts", component: () => import("@components/CompanyProfile/CompanyProfileGeneral") },
        ],
      },
      {
        path: "/settings/modules",
        component: () => import("@components/CompanyProfile/CompanyProfileLayout"),
        children: [
          { path: "", component: () => import("@components/CompanyProfile/CompanyProfileGeneral") },
        ],
      },
      { path: "/settings/team", component: () => import("@components/AllAccounts/AllAccounts") },
      { path: "/settings/integrations", component: () => import("@components/MetaConnections/IntegrationsPage") },
      { path: "/settings/ai", component: () => import("@components/Agent/AgentConfig") },
      { path: "/settings/billing", component: () => import("@components/CompanyProfile/CompanyProfileBilling") },
      ...LEGACY_SETTINGS_REDIRECTS,
      { path: "/expense", redirect: "/operations/expenses" },
      { path: "/investment", redirect: "/operations/investments" },
      { path: "/reporting", redirect: "/operations/reporting" },
      { path: "/settings", redirect: SETTINGS_DEFAULT_PATH },
      { path: "/settings/account-codes", redirect: "/operations/reporting/account-codes" },
      { path: "/settings/expense-category", redirect: "/operations/expense/expense-category" },
      { path: "/settings/investment-category", redirect: "/operations/investment/investment-category" },
      { path: "/settings/service-provider", redirect: "/operations/service/service-companies" },
      { path: "/settings/service-type", redirect: "/operations/service/service-types" },
      { path: "/settings/:tab", redirect: "/operations/reporting/account-codes" },
    ],
  },
].map((group) => ({
  ...group,
  routes: group.routes.filter(({ path }) => isPageVisible(path)),
}));

/** Flat list of all auth routes with a component (for useLazyRouteMap) */
export const AUTH_LAZY_ROUTES = [
  ...AUTH_STANDALONE_ROUTES,
  ...flattenRoutesForLazy(AUTH_ROUTE_GROUPS),
];

/**
 * Sidebar navigation: path, label, and icon key. Order defines display order.
 * Roles are derived from AUTH_ROUTE_GROUPS via getRolesForPath() so they stay in sync with routes.
 * Icon keys are resolved in Sidebar against a local icon map.
 */
export const SIDEBAR_NAV = [
  { path: "/dashboard", label: "Dashboard", iconKey: "dashboard" },
  { path: "/calendar", label: "Calendar", iconKey: "calendar" },
  { path: "/workplace", label: "Inbox", iconKey: "workplace" },
  { path: "/orders", label: "Orders", iconKey: "orders" },
  { path: "/finance", label: "finance.title", iconKey: "finance" },
  { path: "/customers", label: "Customers", iconKey: "customers" },
  { path: "/search", label: "Travel search", iconKey: "travelSearch" },
  { path: "/operations", label: "Operations", iconKey: "operations" },
].filter(({ path }) => isPageVisible(path));

/** Base path of a route (e.g. "/workplace/:customerId?" -> "/workplace") for matching sidebar path */
function getRouteBasePath(routePath) {
  if (!routePath || typeof routePath !== "string") return routePath;
  const i = routePath.indexOf(":");
  return i === -1 ? routePath : routePath.slice(0, i - 1);
}

/**
 * Returns allowedRoles for the first route group that contains a route for the given path.
 * Used by Sidebar so nav visibility stays aligned with AUTH_ROUTE_GROUPS.
 * @param {string} path - Sidebar path (e.g. "/workplace", "/operations")
 * @param {typeof AUTH_ROUTE_GROUPS} [groups=AUTH_ROUTE_GROUPS]
 * @returns {string[]} allowedRoles or [] if path not found
 */
export function getRolesForPath(path, groups = AUTH_ROUTE_GROUPS) {
  for (const group of groups) {
    const hasRoute = group.routes.some((r) => {
      const base = getRouteBasePath(r.path);
      return r.path === path || base === path || Boolean(matchPath(r.path, path)) || r.children?.some(
        (child) => `${r.path}/${child.path}` === path,
      );
    });
    if (hasRoute) return group.allowedRoles;
  }
  return [];
}
